import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Button,
    Typography,
    Card,
    Divider,
    Stack,
    Box,
    Chip,
    Grid,
} from '@mui/joy';
import { AppIcon } from '@app/components/AppIcon';
import AppLoading from '@app/components/AppLoading';
import axios from '@app/config/axios.config';

interface FactureDetails {
    id: number;
    numeroFacture: string;
    libelleFacture: string;
    raisonSocialeFacture: string;
    adresseFacturation: string;
    montantPayeHorsTva: number;
    montantTva: number;
    montantPromesseTva: number;
    montantInt: number;
    montantCent: number;
    datePaiement: string;
    createdAt: string;
    noteAnnonce: string;
    anfChoixAnnonce: string;
    mentionSpeciale: string;
    qrRef: string;
    logo: boolean;
    contractType: string;
    accordDe: string;
    email: string;
    nbParrainage: number;
    parrainage: {
        id: number;
        raisonSociale: string;
        coordonneesCorrespondance: string;
        accordDe: Array<{
            prenom: string;
            nom: string;
        }>;
        dateAccord: string;
        email: string;
        accuseDeReception: boolean;
        noteInterne: string;
        publicite: string;
        reconduction: boolean;
        annonceOk: boolean;
        montantPromesse: number;
        personneMorale: {
            raisonSociale: string;
            email: string;
        };
        personnePhysique: Array<{
            titre: string;
            prenom: string;
            nom: string;
            email: string;
        }>;
        mandat: {
            ref: string;
            club: {
                nom: string;
            };
        };
    };
}

const FactureDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [facture, setFacture] = useState<FactureDetails | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    // Fonction pour formater les dates
    const formatDate = (date: string | null | undefined) => {
        if (!date) return '—';
        try {
            return new Date(date).toLocaleDateString('fr-FR');
        } catch {
            return date;
        }
    };

    // Fonction pour formater les montants
    const formatAmount = (amount: number | null | undefined) => {
        if (amount === null || amount === undefined) return '—';
        return new Intl.NumberFormat('fr-CH', {
            style: 'currency',
            currency: 'CHF',
        }).format(amount);
    };

    useEffect(() => {
        fetchDetails();
    }, [id]);

    const fetchDetails = async () => {
        if (!id) return;

        setLoading(true);
        try {
            const response = await axios.get(`/view-factures/${id}`);
            setFacture(response.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des détails:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                }}
            >
                <AppLoading />
            </Box>
        );
    }

    if (!facture) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography level="h4" color="danger">
                    Facture non trouvée
                </Typography>
                <Button
                    sx={{ mt: 2 }}
                    onClick={() => navigate('/factures')}
                    startDecorator={<AppIcon icon="ArrowLeftIcon" />}
                >
                    Retour à la liste
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
                <Button
                    variant="outlined"
                    color="neutral"
                    startDecorator={<AppIcon icon="ArrowLeftIcon" />}
                    onClick={() => navigate(-1)}
                >
                    Retour
                </Button>
                <Box sx={{ flex: 1 }} />
                <Chip
                    size="sm"
                    color={facture.logo ? 'success' : 'neutral'}
                    variant="soft"
                >
                    {facture.logo ? 'Avec logo' : 'Sans logo'}
                </Chip>
                <Chip
                    size="sm"
                    color={facture.contractType === 'ACVF' ? 'danger' : 'primary'}
                    variant="soft"
                >
                    {facture.contractType}
                </Chip>
            </Stack>

            <Card sx={{ p: 3 }}>
                <Typography level="h4" sx={{ mb: 3 }}>
                    Facture {facture.numeroFacture}
                </Typography>

                {/* Informations de la facture */}
                <Box sx={{ mb: 4 }}>
                    <Typography level="title-md" color="primary" sx={{ mb: 2 }}>
                        Informations de la facture
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={3}>
                        <Grid xs={12} md={6}>
                            <Typography level="title-sm" color="neutral">
                                Numéro de facture
                            </Typography>
                            <Typography level="body-lg" fontWeight="md">
                                {facture.numeroFacture}
                            </Typography>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <Typography level="title-sm" color="neutral">
                                Date de création
                            </Typography>
                            <Typography level="body-lg">
                                {formatDate(facture.createdAt)}
                            </Typography>
                        </Grid>
                        <Grid xs={12}>
                            <Typography level="title-sm" color="neutral">
                                Libellé de la facture
                            </Typography>
                            <Typography level="body-lg">
                                {facture.libelleFacture || '—'}
                            </Typography>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <Typography level="title-sm" color="neutral">
                                Raison sociale facture
                            </Typography>
                            <Typography level="body-lg">
                                {facture.raisonSocialeFacture || '—'}
                            </Typography>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <Typography level="title-sm" color="neutral">
                                Adresse de facturation
                            </Typography>
                            <Typography level="body-lg">
                                {facture.adresseFacturation || '—'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                {/* Montants */}
                <Box sx={{ mb: 4 }}>
                    <Typography level="title-md" color="primary" sx={{ mb: 2 }}>
                        Montants
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={3}>
                        <Grid xs={12} md={3}>
                            <Typography level="title-sm" color="neutral">
                                Montant payé (HT)
                            </Typography>
                            <Typography level="body-lg" fontWeight="md">
                                {formatAmount(facture.montantPayeHorsTva)}
                            </Typography>
                        </Grid>
                        <Grid xs={12} md={3}>
                            <Typography level="title-sm" color="neutral">
                                TVA
                            </Typography>
                            <Typography level="body-lg">
                                {formatAmount(facture.montantTva)}
                            </Typography>
                        </Grid>
                        <Grid xs={12} md={3}>
                            <Typography level="title-sm" color="neutral">
                                Montant promesse + TVA
                            </Typography>
                            <Typography level="body-lg">
                                {formatAmount(facture.montantPromesseTva)}
                            </Typography>
                        </Grid>
                        <Grid xs={12} md={3}>
                            <Typography level="title-sm" color="neutral">
                                Date de paiement
                            </Typography>
                            <Typography level="body-lg">
                                {formatDate(facture.datePaiement)}
                            </Typography>
                        </Grid>
                        <Grid xs={12} md={3}>
                            <Typography level="title-sm" color="neutral">
                                Montant intérêts
                            </Typography>
                            <Typography level="body-lg">
                                {formatAmount(facture.montantInt)}
                            </Typography>
                        </Grid>
                        <Grid xs={12} md={3}>
                            <Typography level="title-sm" color="neutral">
                                Montant centimes
                            </Typography>
                            <Typography level="body-lg">
                                {formatAmount(facture.montantCent)}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                {/* Informations du parrainage */}
                <Box sx={{ mb: 4 }}>
                    <Typography level="title-md" color="primary" sx={{ mb: 2 }}>
                        Informations du parrainage
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={3}>
                        <Grid xs={12} md={6}>
                            <Typography level="title-sm" color="neutral">
                                Raison sociale
                            </Typography>
                            <Typography level="body-lg" fontWeight="md">
                                {facture.parrainage.personneMorale?.raisonSociale || '—'}
                            </Typography>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <Typography level="title-sm" color="neutral">
                                Nom complet
                            </Typography>
                            <Typography level="body-lg">
                                {facture.parrainage.personnePhysique.length > 0 
                                    ? facture.parrainage.personnePhysique.map(p => `${p.titre} ${p.prenom} ${p.nom}`).join(', ')
                                    : '—'
                                }
                            </Typography>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <Typography level="title-sm" color="neutral">
                                Coordonnées correspondance
                            </Typography>
                            <Typography level="body-lg">
                                {facture.parrainage.coordonneesCorrespondance || '—'}
                            </Typography>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <Typography level="title-sm" color="neutral">
                                Téléphone
                            </Typography>
                            <Typography level="body-lg">
                                {facture.parrainage.personnePhysique.length > 0 
                                    ? facture.parrainage.personnePhysique.map(p => p.email).filter(Boolean).join(', ')
                                    : '—'
                                }
                            </Typography>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <Typography level="title-sm" color="neutral">
                                Email
                            </Typography>
                            <Typography level="body-lg">
                                {facture.parrainage.email || facture.parrainage.personneMorale?.email || '—'}
                            </Typography>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <Typography level="title-sm" color="neutral">
                                Accord de
                            </Typography>
                            <Typography level="body-lg">
                                {facture.parrainage.accordDe.length > 0 
                                    ? facture.parrainage.accordDe.map(a => `${a.prenom} ${a.nom}`).join(', ')
                                    : '—'
                                }
                            </Typography>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <Typography level="title-sm" color="neutral">
                                Date d'accord
                            </Typography>
                            <Typography level="body-lg">
                                {formatDate(facture.parrainage.dateAccord)}
                            </Typography>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <Typography level="title-sm" color="neutral">
                                Montant promesse
                            </Typography>
                            <Typography level="body-lg" fontWeight="md">
                                {formatAmount(facture.parrainage.montantPromesse)}
                            </Typography>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <Typography level="title-sm" color="neutral">
                                Club
                            </Typography>
                            <Typography level="body-lg">
                                {facture.parrainage.mandat.club.nom} (
                                {facture.parrainage.mandat.ref})
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                {/* Notes et informations supplémentaires */}
                <Box sx={{ mb: 4 }}>
                    <Typography level="title-md" color="primary" sx={{ mb: 2 }}>
                        Notes et informations
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={3}>
                        <Grid xs={12} md={6}>
                            <Typography level="title-sm" color="neutral">
                                Note annonce
                            </Typography>
                            <Typography level="body-lg">
                                {facture.noteAnnonce || '—'}
                            </Typography>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <Typography level="title-sm" color="neutral">
                                Note interne
                            </Typography>
                            <Typography level="body-lg">
                                {facture.parrainage.noteInterne || '—'}
                            </Typography>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <Typography level="title-sm" color="neutral">
                                Mention spéciale
                            </Typography>
                            <Typography level="body-lg">
                                {facture.mentionSpeciale || '—'}
                            </Typography>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <Typography level="title-sm" color="neutral">
                                QR Référence
                            </Typography>
                            <Typography level="body-lg">
                                {facture.qrRef || '—'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
            </Card>
        </Box>
    );
};

export default FactureDetails; 